/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, AssignConfigToGroupResponse, AssignConfigToGroupSchema } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type AssignConfigToGroupVariables = {
  nodeGroupId: string;
  data: AssignConfigToGroupSchema;
};

export type AssignConfigToGroupMutationConfig = {
  onSuccess?: (data: AssignConfigToGroupResponse, variables: AssignConfigToGroupVariables) => void;
  onError?: (error: unknown, variables: AssignConfigToGroupVariables) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
};

/**
 * Hook for assigning a config to a node group and applying it to all nodes in the group.
 *
 * @param config.onSuccess - Called after successful config assignment
 * @param config.onError - Called if assignment fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for assigning config to node groups
 */
export function useAssignConfigToGroupMutation(config: AssignConfigToGroupMutationConfig) {
  const queryClient = useQueryClient();
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, mutateAsync, isError, error, isPending } = useMutation<
    AssignConfigToGroupResponse,
    ApiError,
    AssignConfigToGroupVariables
  >({
    mutationFn: ({ nodeGroupId, data }: AssignConfigToGroupVariables) => {
      return mgmtPlaneAPIClient.nodeGroups.assignConfigToGroup({
        tenantId: tenantId!,
        nodeGroupId,
        requestBody: data,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['nodeGroups', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['nodeGroups', tenantId, variables.nodeGroupId] });
      queryClient.invalidateQueries({ queryKey: ['nodes', tenantId] });
      config?.onSuccess?.(data, variables);
    },
    onError: config?.onError,
    onSettled: config?.onSettled,
  });

  return { mutate, mutateAsync, isError, error, isPending };
}
